import { apiClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ENV } from '~/constants/config/env';
import { getDevFlagOverrides } from '~/utils/dev-feature-flags';
import { extractDispositionFilename, triggerBrowserDownload } from '~/utils/download-file';

import type {
  ExportFeatureFlagsOptions,
  ExportFeatureFlagsPreview,
  FeatureFlagExportBundle,
  FeatureFlagImportClassification,
  FeatureFlagImportConflictStrategy,
  FeatureFlagImportResult,
} from '../types/feature-flag-export-import.types';
import type {
  CreateFeatureFlagRequest,
  EvaluateFeatureFlagResponse,
  FeatureFlag,
  FeatureFlagDetail,
  FeatureFlagOverride,
  ResolvedFeatureFlags,
  SearchFeatureFlagsRequest,
  SearchFeatureFlagsResponse,
  UpdateFeatureFlagRequest,
  UpsertFeatureFlagOverrideRequest,
} from '../types/feature-flag.types';

/**
 * Feature-flag API client wrappers — all calls go through `apiClient`
 * (identity-service). Admin endpoints require admin scope; backend enforces.
 */

/** Resolved flag map for the current user (key → boolean). The wire response
 *  already matches `ResolvedFeatureFlags` shape; we only normalise the
 *  null/undefined edges so consumers can rely on non-nullable fields.
 *  In dev mode, local overrides (env var + localStorage) are merged on top. */
export async function getMyFeatureFlags(): Promise<ResolvedFeatureFlags> {
  const raw = await apiClient
    .get(endpoints.featureFlags.me())
    .json<Partial<ResolvedFeatureFlags>>();
  return {
    flags: { ...(raw.flags ?? {}), ...getDevFlagOverrides(ENV.VITE_DEV_FEATURE_FLAGS) },
    generation: raw.generation ?? '',
  };
}

/** Paginated, filtered admin search of feature flags. */
export async function searchFeatureFlags(
  payload: SearchFeatureFlagsRequest,
): Promise<SearchFeatureFlagsResponse> {
  return apiClient
    .post(endpoints.featureFlags.search(), { json: payload })
    .json<SearchFeatureFlagsResponse>();
}

export async function createFeatureFlag(payload: CreateFeatureFlagRequest): Promise<FeatureFlag> {
  return apiClient.post(endpoints.featureFlags.create(), { json: payload }).json<FeatureFlag>();
}

/** Detail (flag + its overrides) for the overrides drawer. */
export async function getFeatureFlag(id: string): Promise<FeatureFlagDetail> {
  return apiClient.get(endpoints.featureFlags.byId(id)).json<FeatureFlagDetail>();
}

export async function updateFeatureFlag(
  id: string,
  payload: UpdateFeatureFlagRequest,
): Promise<FeatureFlag> {
  return apiClient.patch(endpoints.featureFlags.byId(id), { json: payload }).json<FeatureFlag>();
}

export async function deleteFeatureFlag(id: string): Promise<void> {
  await apiClient.delete(endpoints.featureFlags.byId(id));
}

/** Flip status between ACTIVE ↔ ARCHIVED — backend toggles atomically. */
export async function toggleFeatureFlagStatus(id: string): Promise<FeatureFlag> {
  return apiClient.patch(endpoints.featureFlags.toggle(id)).json<FeatureFlag>();
}

/** Resolve a single flag for a specific subsidiary (default vs override). */
export async function evaluateFeatureFlag(
  id: string,
  subsidiaryId: string,
): Promise<EvaluateFeatureFlagResponse> {
  return apiClient
    .get(endpoints.featureFlags.evaluate(id), {
      searchParams: { subsidiaryId },
    })
    .json<EvaluateFeatureFlagResponse>();
}

export async function upsertFeatureFlagOverride(
  id: string,
  subId: string,
  payload: UpsertFeatureFlagOverrideRequest,
): Promise<FeatureFlagOverride> {
  return apiClient
    .put(endpoints.featureFlags.override(id, subId), { json: payload })
    .json<FeatureFlagOverride>();
}

export async function deleteFeatureFlagOverride(id: string, subId: string): Promise<void> {
  await apiClient.delete(endpoints.featureFlags.override(id, subId));
}

// ---------------------------------------------------------------------------
// Export / import — move Feature Flag definitions between environments (superAdmin).
// ---------------------------------------------------------------------------

/** Counts-only preview of a Feature Flags export for the given filters — no row payload. */
export async function previewFeatureFlagExport(
  options: ExportFeatureFlagsOptions,
): Promise<ExportFeatureFlagsPreview> {
  return apiClient
    .post(endpoints.featureFlags.exportPreview(), { json: options })
    .json<ExportFeatureFlagsPreview>();
}

/** Downloads the full Feature Flags export bundle and saves it as a JSON file. */
export async function exportFeatureFlags(options: ExportFeatureFlagsOptions): Promise<void> {
  const response = await apiClient.post(endpoints.featureFlags.export(), { json: options });
  const blob = await response.blob();
  const filename =
    extractDispositionFilename(response.headers.get('content-disposition')) ??
    'feature-flags-export.json';
  triggerBrowserDownload(blob, filename);
}

/**
 * Read-only classification of an uploaded bundle (create/update/unchanged/blocked)
 * — drives the diff table. Runs the same logic `/admin/feature-flags/import` will
 * run, but never writes.
 */
export async function previewFeatureFlagImport(
  bundle: FeatureFlagExportBundle,
): Promise<FeatureFlagImportClassification> {
  return apiClient
    .post(endpoints.featureFlags.importPreview(), { json: { bundle } })
    .json<FeatureFlagImportClassification>();
}

/** Commits the import. Always resubmit the same bundle used for the preview call. */
export async function importFeatureFlags(options: {
  bundle: FeatureFlagExportBundle;
  conflictStrategy?: FeatureFlagImportConflictStrategy;
}): Promise<FeatureFlagImportResult> {
  return apiClient
    .post(endpoints.featureFlags.import(), { json: options })
    .json<FeatureFlagImportResult>();
}
